import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  IconButton,
  InputBase,
  CircularProgress,
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

// Palette mirrored from gate-backstage's /ai-assistant ChatUI.
const useStyles = makeStyles({
  launcher: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#9894f9',
    color: '#ffffff',
    boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
    zIndex: 1300,
    '&:hover': { backgroundColor: '#827df7' },
  },
  panel: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 400,
    maxWidth: 'calc(100vw - 32px)',
    height: 600,
    maxHeight: 'calc(100vh - 48px)',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#181829',
    borderRadius: 16,
    border: '1px solid #2a2a40',
    boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
    overflow: 'hidden',
    zIndex: 1300,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 12px 12px 18px',
    background: 'linear-gradient(-137deg, #9894f9 0%, #6C69B1 100%)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 700,
  },
  closeButton: {
    color: 'rgba(255,255,255,0.8)',
    padding: 6,
    '&:hover': { color: '#ffffff' },
  },
  messageList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '16px 16px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: '#252538', borderRadius: 2 },
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: '#9A9AA3',
    userSelect: 'none',
    textAlign: 'center',
    padding: '0 24px',
  },
  emptyStateTitle: { fontSize: 18, fontWeight: 700, color: '#ffffff' },
  userBubble: {
    alignSelf: 'flex-end',
    maxWidth: '80%',
    padding: '10px 14px',
    borderRadius: 18,
    borderBottomRightRadius: 4,
    backgroundColor: '#373745',
    color: '#ffffff',
    fontSize: 14,
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  assistantBubble: {
    alignSelf: 'flex-start',
    maxWidth: '92%',
    wordBreak: 'break-word',
    display: 'flex',
    flexDirection: 'column',
  },
  copyButton: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    marginTop: 6,
    padding: '2px 8px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: '#5E5E6B',
    fontSize: 12,
    cursor: 'pointer',
    '&:hover': { color: '#9894f9' },
  },
  sources: {
    marginTop: 8,
    fontSize: 11,
    color: '#7a7a90',
    lineHeight: 1.6,
  },
  sourceLink: {
    color: '#9894f9',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  markdownBody: {
    color: '#e2e2f4',
    fontSize: 14,
    lineHeight: 1.7,
    '& p': { margin: '0 0 8px 0', '&:last-child': { marginBottom: 0 } },
    '& h1, & h2, & h3, & h4': {
      color: '#ffffff',
      fontWeight: 600,
      margin: '12px 0 6px 0',
    },
    '& h1': { fontSize: 17 },
    '& h2': { fontSize: 15 },
    '& h3, & h4': { fontSize: 14 },
    '& ul, & ol': { paddingLeft: 20, margin: '4px 0 8px 0' },
    '& li': { marginBottom: 4 },
    '& code': {
      backgroundColor: '#252538',
      color: '#c8c8f0',
      padding: '1px 5px',
      borderRadius: 4,
      fontSize: 13,
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: '#252538',
      borderRadius: 8,
      padding: '12px 16px',
      overflowX: 'auto',
      margin: '8px 0',
      '& code': { background: 'none', padding: 0, fontSize: 13 },
    },
    '& blockquote': {
      borderLeft: '3px solid #4B4B59',
      margin: '8px 0',
      paddingLeft: 12,
      color: '#9A9AA3',
    },
    '& strong': { color: '#ffffff', fontWeight: 600 },
    '& a': { color: '#9894f9', textDecoration: 'none' },
    '& hr': { border: 'none', borderTop: '1px solid #252538', margin: '12px 0' },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '8px 0',
      fontSize: 13,
    },
    '& th': {
      backgroundColor: '#252538',
      color: '#ffffff',
      padding: '7px 12px',
      border: '1px solid #3a3a52',
      fontWeight: 600,
      textAlign: 'left',
    },
    '& td': {
      padding: '6px 12px',
      border: '1px solid #3a3a52',
      color: '#e2e2f4',
      verticalAlign: 'top',
    },
    '& tr:nth-child(even) td': { backgroundColor: '#1e1e34' },
  },
  typingIndicator: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: '#44445e',
    fontSize: 13,
    padding: '4px 2px',
  },
  inputWrapper: { padding: '10px 14px 12px', backgroundColor: '#181829' },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#181829',
    borderRadius: 12,
    border: '1px solid #FFFFFF',
    padding: '6px 6px 6px 16px',
    '&:focus-within': { borderColor: '#9894f9' },
  },
  input: { flex: 1, color: '#e2e2f4', fontSize: 14, lineHeight: 1.5 },
  sendButton: {
    padding: 6,
    flexShrink: 0,
    '&:not(.Mui-disabled)': { color: '#ffffff' },
    '&:hover:not(.Mui-disabled)': { color: '#9894f9' },
    '&.Mui-disabled': { color: '#9A9AA3' },
  },
});

type Source = { title: string; path: string };
type Msg = { role: 'user' | 'assistant'; content: string; sources?: Source[] };

// Public docs-v2 URL for a doc's relative path, e.g.
// "clinical/graphql/01_api.md" -> "<origin>/docs-v2/clinical/graphql/01_api".
// Uses the current origin so links stay within the same environment.
const docUrl = (path: string) =>
  `${window.location.origin}/docs-v2/${path.replace(/\.(md|mdx)$/i, '')}`;

function renderMarkdown(content: string): string {
  // The model sometimes concatenates table rows with || (no newline). Restore them.
  const normalized = content.replace(/\|\|/g, '|\n|');
  return DOMPurify.sanitize(String(marked.parse(normalized)));
}

const CopyButton = ({ content, className }: { content: string; className: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button type="button" className={className} onClick={handleCopy}>
      {copied ? (
        <>
          <CheckIcon style={{ fontSize: 13 }} /> Copied
        </>
      ) : (
        <>
          <FileCopyOutlinedIcon style={{ fontSize: 13 }} /> Copy
        </>
      )}
    </button>
  );
};

export const DocsAssistantWidget = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Msg[]>([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && !loading) inputRef.current?.focus();
  }, [open, loading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('docs-assistant');
      const res = await fetch(`${baseUrl}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Error ${res.status}`);
      setMessages(m => [
        ...m,
        { role: 'assistant', content: data.answer, sources: data.sources },
      ]);
    } catch (e) {
      setMessages(m => [
        ...m,
        { role: 'assistant', content: `⚠️ ${(e as Error).message}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!open) {
    return (
      <IconButton
        className={classes.launcher}
        onClick={() => setOpen(true)}
        aria-label="Open documentation assistant"
      >
        <ChatBubbleOutlineIcon />
      </IconButton>
    );
  }

  return (
    <Box className={classes.panel}>
      <Box className={classes.header}>
        <Box className={classes.headerTitle}>AI Assistant</Box>
        <IconButton
          className={classes.closeButton}
          onClick={() => setOpen(false)}
          aria-label="Close documentation assistant"
        >
          <CloseIcon style={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box className={classes.messageList}>
        {messages.length === 0 && !loading ? (
          <Box className={classes.emptyState}>
            <Box className={classes.emptyStateTitle}>How can I help you?</Box>
            <Box style={{ fontSize: 13 }}>
              Ask me anything about the documentation.
            </Box>
          </Box>
        ) : (
          messages.map((msg, i) =>
            msg.role === 'user' ? (
              <Box key={i} className={classes.userBubble}>
                {msg.content}
              </Box>
            ) : (
              <Box key={i} className={classes.assistantBubble}>
                <div
                  className={classes.markdownBody}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                />
                {!!msg.sources?.length && (
                  <Box className={classes.sources}>
                    Sources:{' '}
                    {msg.sources.map((s, idx) => (
                      <React.Fragment key={s.path}>
                        {idx > 0 && ', '}
                        <a
                          href={docUrl(s.path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.sourceLink}
                        >
                          {s.title}
                        </a>
                      </React.Fragment>
                    ))}
                  </Box>
                )}
                {!!msg.content && (
                  <CopyButton content={msg.content} className={classes.copyButton} />
                )}
              </Box>
            ),
          )
        )}
        {loading && (
          <Box className={classes.typingIndicator}>
            <CircularProgress size={11} style={{ color: '#5a58a0' }} />
            Thinking…
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      <Box className={classes.inputWrapper}>
        <Box className={classes.inputRow}>
          <InputBase
            className={classes.input}
            multiline
            maxRows={6}
            placeholder="Type your request..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
          />
          <IconButton
            className={classes.sendButton}
            onClick={handleSend}
            disabled={!input.trim() || loading}
          >
            <SendIcon style={{ fontSize: 22 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
